/**
 * Registro dos tenants SEMEADOS pela casa — os demos de avaliação por idioma e
 * os tenants de teste. Fonte única de "quem são, em que idioma, de que país".
 *
 * Origem: #1003 — 2026-10-19 12:15 +0200 (owner: um demo por idioma com id
 * próprio — `demo-account-pt/it/en` — e os demos antigos `BR-01010001000180` /
 * `IT-98765432103` passando a ser exclusivamente de teste).
 *
 * Por quê: até a #1003 os dois tenants simulados eram uma lista literal em
 * `labelPurge.ts`, o idioma do catálogo era "pt-BR para todos" (o tenant IT
 * tinha produtos em português) e criar um demo era trabalho manual sem roteiro.
 * Com o registro, criar o demo de um idioma novo é: uma entrada aqui + o
 * catálogo traduzido do simulador + o script `create-seed-tenant.ts` — o
 * roteiro está em `docs/demo-tenants-runbook.md` (e o de tenant de teste em
 * `docs/test-tenants-runbook.md`).
 *
 * Contrato:
 *  - `DEMO_TENANT_IDS` / `TEST_TENANT_IDS` são tuplas `as const` — o callable
 *    do simulador faz `z.enum(...)` sobre a união delas e precisa dos literais.
 *  - `SEED_TENANT_SPECS` é um `Record` EXAUSTIVO por id: acrescentar um id sem
 *    spec não compila.
 *  - `language` é o idioma do catálogo do simulador (produtos, regras,
 *    fornecedores, responsáveis), não o `locale` do tenant — o tenant de teste
 *    IT tem `language: 'pt'` porque o catálogo dele já nasceu em português e
 *    produto existente nunca é sobrescrito.
 *  - `simTimeZone`: `TENANT` = a curva do dia do simulador segue o fuso do
 *    próprio tenant (demos novos, para o lead ver a cozinha trabalhando no
 *    horário dele); `SAO_PAULO` = fuso fixo da decisão de 2026-07-07 (#108),
 *    mantido nos dois tenants de teste.
 *
 * Usado por: `labelPurge.ts` (`LABEL_PURGING_TENANT_IDS` deriva daqui →
 * simulador, purga, `monitorDailyOpsHealth`, relatórios, monitor de uso);
 * `apps/functions/src/admin/testTenantUsageSimulator.ts` (idioma e fuso por
 * tenant); `apps/functions/scripts/create-seed-tenant.ts` (criação);
 * `apps/crm-functions/src/crmDemoTenants.ts` (tenants de teste fora do
 * dropdown de demo, via `isInternalTestTenant`); `crmClientsList.ts` e
 * `crmCities.ts` (tenants de teste fora da carteira do CRM).
 *
 * Arquitetura: sem `zod` de propósito, como `labelPurge.ts` — os scripts do
 * repo importam este módulo com `tsx`, direto do source. O id do demo NÃO segue
 * o formato `{CC}-{taxId}` dos clientes: é escolhido (`demo-account-<lang>`),
 * e o país vem do campo `countryCode` do documento, nunca do id.
 *
 * Falhas conhecidas:
 *  - Até 2026-10-19 (#1003, #994 A4): o demo IT se chamava "Android Audit/Test"
 *    e mostrava produtos em português ao lead italiano.
 *
 * Dicas:
 *  - Id novo aqui entra AUTOMATICAMENTE no simulador agendado na próxima
 *    publicação — crie o tenant com o script antes ou logo depois do deploy
 *    (o simulador pula tenant inexistente com `testTenantUsageSim_tenant_not_active`).
 *  - Não mude o id de um tenant existente: Firestore não renomeia documento e
 *    o id está no e-mail Auth dos STAFF, nos paths do Storage e no CRM. Crie
 *    outro (decisão da #1003).
 *  - `TEST_MODE_BILLING_TENANT_IDS` é OUTRA lista (dinheiro de teste) e exige
 *    aval do owner; estar aqui não põe ninguém em sandbox.
 *
 * Issues: #1003 (origem), #354 (lista única do simulador), #108 (simulador),
 * #994 (demo por língua no CRM).
 *
 * Histórico:
 *  - 2026-10-19 12:15 +0200 — (#1003) criado.
 *  - 2026-10-19 18:04 +0200 — (#1006) tenants de teste renomeados para dizer a
 *    função; o cadastro de TODOS os tenants de teste é `testTenants.ts`.
 */

/** Ids dos demos de avaliação — um por idioma, mostrados a prospects pelo CRM. */
export const DEMO_TENANT_IDS = ['demo-account-pt', 'demo-account-it', 'demo-account-en'] as const;

/**
 * Ids dos tenants de teste internos. Eram os demos até a #1003; não mudaram de
 * id. `IT-98765432103` é a bancada do Pixel/iPhone (OWNER `android@`) e está
 * na allowlist de billing sandbox; `BR-01010001000180` é o tenant de teste BR.
 * A ordem (IT primeiro) é a mesma da antiga `LABEL_PURGING_TENANT_IDS`, que os
 * testes do simulador indexam.
 */
export const TEST_TENANT_IDS = ['IT-98765432103', 'BR-01010001000180'] as const;

export type DemoTenantId = (typeof DEMO_TENANT_IDS)[number];
export type TestTenantId = (typeof TEST_TENANT_IDS)[number];
export type SeedTenantId = DemoTenantId | TestTenantId;

/** Para que o tenant semeado existe. */
export const SeedTenantPurpose = {
  /** Demo de avaliação: o CRM concede acesso a prospects (`crm_demo_tenants`). */
  DEMO: 'DEMO',
  /** Teste interno: aparelhos de QA, e2e, simulador; nunca mostrado a lead. */
  TEST: 'TEST',
} as const;
export type SeedTenantPurpose = (typeof SeedTenantPurpose)[keyof typeof SeedTenantPurpose];

/**
 * Idiomas com catálogo no simulador (`apps/functions/src/admin/simCatalog/`).
 * Idioma novo: acrescentar aqui, criar o arquivo do catálogo, e — se for um
 * demo — conferir que existe em `CrmDemoLanguageSchema` (`crm.ts`); o teste
 * `seedTenants.test.ts` cobra.
 */
export const SEED_TENANT_LANGUAGES = ['pt', 'it', 'en'] as const;
export type SeedTenantLanguage = (typeof SEED_TENANT_LANGUAGES)[number];

/** Fuso da curva do dia do simulador (ver cabeçalho do módulo). */
export const SimTimeZoneMode = {
  TENANT: 'TENANT',
  SAO_PAULO: 'SAO_PAULO',
} as const;
export type SimTimeZoneMode = (typeof SimTimeZoneMode)[keyof typeof SimTimeZoneMode];

/**
 * Ficha de um tenant semeado. `name`/`countryCode`/`locale`/`timeZone` são o
 * que o script de criação grava no documento; `countryCode` é imutável depois
 * (define moeda, telefone e fornecedores do simulador).
 */
export type SeedTenantSpec = {
  readonly tenantId: SeedTenantId;
  readonly purpose: SeedTenantPurpose;
  readonly language: SeedTenantLanguage;
  readonly name: string;
  readonly countryCode: string;
  /** Um valor de `TenantLocaleSchema`; também vira `appLocale` no demo. */
  readonly locale: string;
  /** IANA. */
  readonly timeZone: string;
  readonly simTimeZone: SimTimeZoneMode;
};

/**
 * As fichas. Demos: país e fuso decididos pelo owner em 2026-10-19 (#1003) —
 * EN = Irlanda (euro, zona de preço UE). Teste: o que já estava gravado nos
 * documentos; o script não recria tenant existente, só alinha o `name`.
 */
export const SEED_TENANT_SPECS: Readonly<Record<SeedTenantId, SeedTenantSpec>> = {
  'demo-account-pt': {
    tenantId: 'demo-account-pt',
    purpose: SeedTenantPurpose.DEMO,
    language: 'pt',
    name: 'Conta de avaliação em português',
    countryCode: 'BR',
    locale: 'pt-BR',
    timeZone: 'America/Sao_Paulo',
    simTimeZone: SimTimeZoneMode.TENANT,
  },
  'demo-account-it': {
    tenantId: 'demo-account-it',
    purpose: SeedTenantPurpose.DEMO,
    language: 'it',
    name: 'Account di valutazione in italiano',
    countryCode: 'IT',
    locale: 'it',
    timeZone: 'Europe/Rome',
    simTimeZone: SimTimeZoneMode.TENANT,
  },
  'demo-account-en': {
    tenantId: 'demo-account-en',
    purpose: SeedTenantPurpose.DEMO,
    language: 'en',
    name: 'Evaluation account in English',
    countryCode: 'IE',
    locale: 'en',
    timeZone: 'Europe/Dublin',
    simTimeZone: SimTimeZoneMode.TENANT,
  },
  'IT-98765432103': {
    tenantId: 'IT-98765432103',
    purpose: SeedTenantPurpose.TEST,
    language: 'pt',
    // #1006: nome diz a função (era "Android Audit/Test"); o id não muda.
    name: 'Teste — bancada Pixel/iPhone (IT)',
    countryCode: 'IT',
    locale: 'it',
    timeZone: 'Europe/Rome',
    simTimeZone: SimTimeZoneMode.SAO_PAULO,
  },
  'BR-01010001000180': {
    tenantId: 'BR-01010001000180',
    purpose: SeedTenantPurpose.TEST,
    language: 'pt',
    // #1006: nome diz a função (era "Teste BR (QA)").
    name: 'Teste — QA e simulador (BR)',
    countryCode: 'BR',
    locale: 'pt-BR',
    timeZone: 'America/Sao_Paulo',
    simTimeZone: SimTimeZoneMode.SAO_PAULO,
  },
};

/** Ficha do id, ou `null` se não é tenant semeado (cliente real). */
export function seedTenantSpecOf(tenantId: string): SeedTenantSpec | null {
  return Object.prototype.hasOwnProperty.call(SEED_TENANT_SPECS, tenantId)
    ? SEED_TENANT_SPECS[tenantId as SeedTenantId]
    : null;
}

const TEST_IDS: ReadonlySet<string> = new Set(TEST_TENANT_IDS);

/**
 * `true` para os tenants de teste internos — nunca oferecidos como demo no CRM
 * nem listados como cliente. Eles têm `isDemo` (para ficar fora de MRR e
 * relatórios de negócio), então `isDemo` sozinho não os distingue de um demo.
 */
export function isInternalTestTenant(tenantId: string): boolean {
  return TEST_IDS.has(tenantId);
}
